import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { MapPin, Mail, Phone, Clock } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    subject: 'Export',
    message: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', message: '' });

    try {
      await axios.post(`${API}/contact`, formData);
      setStatus({ type: 'success', message: t.contact.success });
      setFormData({
        name: '',
        company: '',
        email: '',
        subject: 'Export',
        message: ''
      });
    } catch (error) {
      console.error('Error sending message:', error);
      setStatus({ type: 'error', message: t.contact.error });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-stone-800 to-stone-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="contact-title">{t.contact.title}</h1>
          <p className="text-xl">{t.contact.subtitle}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold text-stone-800 mb-6">{t.contact.form_title}</h2>
            
            {status.message && (
              <div 
                className={`mb-6 p-4 rounded-lg ${
                  status.type === 'success' 
                    ? 'bg-green-50 text-green-700 border border-green-200' 
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}
                data-testid="form-status-message"
              >
                {status.message}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t.contact.name} *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  data-testid="contact-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t.contact.company}
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  data-testid="contact-company-input"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t.contact.email} *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  data-testid="contact-email-input"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t.contact.subject} *
                </label>
                <select
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  data-testid="contact-subject-select"
                >
                  <option value="Export">Export</option>
                  <option value="Devis Industriel">{language === 'fr' ? 'Devis Industriel' : 'Industrial Quote'}</option>
                  <option value="Distribution">Distribution</option>
                  <option value="Autre">{language === 'fr' ? 'Autre' : 'Other'}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t.contact.message} *
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="5"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  data-testid="contact-message-textarea"
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-3 rounded-lg transition-colors disabled:bg-gray-400"
                data-testid="contact-submit-button"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {language === 'fr' ? 'Envoi...' : 'Sending...'}
                  </span>
                ) : (
                  t.contact.send
                )}
              </button>
            </form>
          </div>

          {/* Contact Information */}
          <div>
            {/* Headquarters */}
            <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
              <h2 className="text-2xl font-bold text-stone-800 mb-6">{t.contact.headquarters}</h2>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <MapPin className="text-amber-600 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <p className="text-gray-700">Zone Industrielle Sidi Salem</p>
                    <p className="text-gray-700">Route de Gabes km 3</p>
                    <p className="text-gray-700">Sfax, Tunisie</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Clock className="text-amber-600 flex-shrink-0" size={20} />
                  <p className="text-gray-700">{t.contact.hours}</p>
                </div>
              </div>
            </div>

            {/* Contact by Division */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h3 className="text-xl font-bold text-stone-800 mb-4">
                {language === 'fr' ? 'Contacts par Pôle' : 'Contact by Division'}
              </h3>
              <div className="space-y-4 text-sm">
                <div>
                  <p className="font-semibold text-stone-800">
                    {language === 'fr' ? 'Direction Générale' : 'General Management'}
                  </p>
                  <div className="flex items-center space-x-2 text-gray-600 mt-1">
                    <Mail size={14} />
                    <a href="mailto:contact@barkatis-group.com" className="hover:text-amber-600">contact@barkatis-group.com</a>
                  </div>
                </div>
                <div>
                  <p className="font-semibold text-stone-800">Tout pour la Céramique</p>
                  <div className="flex items-center space-x-2 text-gray-600 mt-1">
                    <Mail size={14} />
                    <a href="mailto:contact@tpc.tn" className="hover:text-amber-600">contact@tpc.tn</a>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-600 mt-1">
                    <Phone size={14} />
                    <span>+216 36 400 920</span>
                  </div>
                </div>
                <div>
                  <p className="font-semibold text-stone-800">
                    {language === 'fr' ? 'Pôle Agro-Alimentaire' : 'Agri-Food Division'}
                  </p>
                  <div className="flex items-center space-x-2 text-gray-600 mt-1">
                    <Mail size={14} />
                    <a href="mailto:contact@Legatodoro.com" className="hover:text-amber-600">contact@Legatodoro.com</a>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-600 mt-1">
                    <Phone size={14} />
                    <span>+216 54 111 333</span>
                  </div>
                </div>
                <div>
                  <p className="font-semibold text-stone-800">Sodever (Art & Design)</p>
                  <div className="flex items-center space-x-2 text-gray-600 mt-1">
                    <Mail size={14} />
                    <a href="mailto:contact@sodever.tn" className="hover:text-amber-600">contact@sodever.tn</a>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-600 mt-1">
                    <Phone size={14} />
                    <span>+216 36 400 921</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="bg-white rounded-lg shadow-lg p-4 mt-6">
              <div className="aspect-video w-full">
                <iframe
                  src="https://www.google.com/maps?q=34.7161744,10.7337036&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Barkatis Group Location"
                  data-testid="google-map"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;